import type { CalendarYearReturn } from 'shared';

import { Table } from '../ui/Table';
import { TableCell } from '../ui/TableCell';
import { TableHeaderCell } from '../ui/TableHeaderCell';

export interface AnnualReturnsRow {
  accountId: string;
  accountName: string;
  detail: string;
  state: 'ready' | 'loading' | 'error';
  years: readonly CalendarYearReturn[];
}

function signedPct(value: string): string {
  const number = Number(value);
  return `${number > 0 ? '+' : ''}${number.toFixed(1)}%`;
}

function toneClass(value: string): string {
  const number = Number(value);
  if (number > 0) return 'text-success';
  if (number < 0) return 'text-danger';
  return 'text-text';
}

function cellTitle(year: CalendarYearReturn): string {
  if (year.returnPct === null) return year.unavailableReason ?? 'Unavailable';
  const range = `${year.startDate ?? '?'} → ${year.endDate ?? '?'}`;
  return year.partial ? `Partial year: tracking began ${year.startDate ?? '?'} (${range})` : range;
}

function YearCell({ year }: { year: CalendarYearReturn | undefined }) {
  if (!year) {
    return (
      <TableCell numeric className="py-1.5 text-text-muted">
        <span aria-hidden="true">·</span>
        <span className="sr-only">No data</span>
      </TableCell>
    );
  }
  if (year.returnPct === null) {
    return (
      <TableCell numeric className="py-1.5 text-text-muted" title={cellTitle(year)}>
        <span aria-hidden="true">—</span>
        <span className="sr-only">Unavailable: {year.unavailableReason}</span>
      </TableCell>
    );
  }
  return (
    <TableCell
      numeric
      className={`whitespace-nowrap py-1.5 font-medium ${toneClass(year.returnPct)}`}
      title={cellTitle(year)}
    >
      {signedPct(year.returnPct)}
      {year.partial ? (
        <>
          <span aria-hidden="true">*</span>
          <span className="sr-only"> (partial year from {year.startDate})</span>
        </>
      ) : null}
    </TableCell>
  );
}

export function AnnualReturnsTable({
  rows,
  label,
}: {
  rows: readonly AnnualReturnsRow[];
  label: string;
}) {
  const currentYear = Math.max(
    ...rows.flatMap((row) => row.years.filter((year) => year.yearToDate).map((year) => year.year)),
    Number.NEGATIVE_INFINITY,
  );
  const columns = [...new Set(rows.flatMap((row) => row.years.map((year) => year.year)))].sort(
    (left, right) => right - left,
  );

  if (rows.length === 0) {
    return <p className="text-sm text-text-muted">No accounts in this view.</p>;
  }

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded border border-border">
        <Table className="text-sm" aria-label={label}>
          <thead className="bg-surface-raised">
            <tr>
              <TableHeaderCell className="sticky left-0 bg-surface-raised">Account</TableHeaderCell>
              {columns.map((year) => (
                <TableHeaderCell key={year} numeric className="whitespace-nowrap">
                  {year === currentYear ? `${year} YTD` : year}
                </TableHeaderCell>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.accountId} className="border-t border-border">
                <TableCell className="sticky left-0 min-w-[10rem] bg-surface py-1.5">
                  <div className="font-medium">{row.accountName}</div>
                  <div className="text-xs text-text-muted">{row.detail}</div>
                </TableCell>
                {row.state !== 'ready' || row.years.length === 0 ? (
                  <TableCell
                    className="py-1.5 text-text-muted"
                    colSpan={Math.max(columns.length, 1)}
                  >
                    {row.state === 'loading'
                      ? 'Loading…'
                      : row.state === 'error'
                        ? 'Could not load returns.'
                        : 'No valuation history yet.'}
                  </TableCell>
                ) : (
                  columns.map((column) => (
                    <YearCell key={column} year={row.years.find((year) => year.year === column)} />
                  ))
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <p className="text-xs text-text-muted">
        Time-weighted returns in each account&apos;s own currency, so deposits and withdrawals are
        not counted as gains. * Partial year: tracking began after 1 January. — means a valuation
        near the year boundary is missing; hover for details.
      </p>
    </div>
  );
}
